import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient, PostgrestError } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import {
  computeForecastFallback,
  computeNextBatchAt,
  computeNextRunAt,
  computeTodayBatchAt,
  type InviteForecast,
  type InviteForecastMember,
  type InviteFunnel,
} from "@/utils/inviteForecast";
import type { CustomerMarketing } from "@/types/sms";

const FORECAST_QUERY_KEY = ["sms-invite-forecast"];

/** Postgres/PostgREST codes for "the RPC isn't there yet" (migration unapplied). */
function isMissingRpc(error: PostgrestError | null): boolean {
  if (!error) return false;
  return error.code === "42883" || error.code === "PGRST202" || /find the function|does not exist/i.test(error.message);
}

interface ForecastRpcRow {
  eligible_now: number;
  funnel: InviteFunnel;
  /** Absent on the first get_invite_forecast() (20260915120000), which
   *  anchored on the next batch; the hook falls back rather than show it. */
  today_batch_at?: string | null;
  next_batch_at: string | null;
  next_so_far?: number | null;
  members?: InviteForecastMember[] | null;
  typical_by_dow: Record<string, number> | null;
}

/** Pages every v_customer_marketing row's forecast-relevant columns so the
 *  client fallback classifies the full order window, not a truncated page. */
async function fetchFallbackMembers(sb: SupabaseClient): Promise<ForecastFallbackMember[]> {
  const pageSize = 1000;
  const members: ForecastFallbackMember[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await sb
      .from("v_customer_marketing")
      .select(
        "customer_phone, display_name, opt_in_status, messaging_status, never_invite, invite_count, last_invited_at, last_order_at, deleted_at",
      )
      .order("customer_phone", { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = (data ?? []) as ForecastFallbackMember[];
    members.push(...rows);
    if (rows.length < pageSize) break;
  }
  return members;
}

type ForecastFallbackMember = Pick<
  CustomerMarketing,
  | "customer_phone"
  | "display_name"
  | "opt_in_status"
  | "messaging_status"
  | "never_invite"
  | "invite_count"
  | "last_invited_at"
  | "last_order_at"
  | "deleted_at"
>;

/**
 * The Invitation tab's forecast: today's batch cohort (fixed all day —
 * everyone whose latest order fell on yesterday's store-local calendar day),
 * who is in it and why the rest don't get the invite, plus how many are
 * lined up for the next batch once
 * today's has passed. Reads the get_invite_forecast() RPC (the source of
 * truth — it uses the invite materialiser's own eligibility predicate). While
 * the RPC migration is not yet applied, it falls back to classifying
 * v_customer_marketing client-side (computeForecastFallback) so the hero and
 * funnel still render. The fallback is an approximation: it can't compute the
 * trailing per-weekday "typical" numbers (those need SQL), and it has no
 * per-phone invite history (v_customer_invite_status ships with the RPC), so
 * it doesn't apply the contactless invite-history cap and may overcount
 * will_invite until the RPC lands.
 */
export const useInviteForecast = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<InviteForecast>({
    queryKey: FORECAST_QUERY_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb.rpc("get_invite_forecast");
      if (!error) {
        const row = (Array.isArray(data) ? data[0] : data) as ForecastRpcRow | undefined;
        // An RPC row without today_batch_at is the superseded next-batch
        // version — fall through to the fallback so the number never slides.
        if (row && row.today_batch_at !== undefined) {
          return {
            eligible_now: row.eligible_now ?? 0,
            funnel: row.funnel,
            today_batch_at: row.today_batch_at,
            next_batch_at: row.next_batch_at,
            next_so_far: row.next_so_far ?? null,
            members: row.members ?? [],
            typical_by_dow: row.typical_by_dow ?? {},
          };
        }
      }
      if (error && !isMissingRpc(error)) throw error;

      // Fallback: the RPC isn't deployed yet (or is the superseded version).
      // Reproduce it client-side.
      const [{ data: settings, error: sErr }, { data: rotation, error: rErr }] = await Promise.all([
        sb.from("sms_marketing_settings").select("*").single(),
        sb.from("sms_invite_rotations").select("*").maybeSingle(),
      ]);
      if (sErr) throw sErr;
      if (rErr) throw rErr;

      const cooldownDays = settings?.invite_followup_cooldown_days ?? 30;
      const followUps = rotation?.follow_ups ?? 1;
      const sendTime = rotation?.send_time ?? "11:00:00";
      const timezone = settings?.timezone ?? "America/Los_Angeles";
      const quietStart = settings?.quiet_hours_start ?? 8;
      const quietEnd = settings?.quiet_hours_end ?? 21;
      const now = new Date();
      const todayBatchAt = rotation ? computeTodayBatchAt(sendTime, timezone, now) : null;
      const nextBatchAt = rotation ? computeNextBatchAt(sendTime, timezone, quietStart, quietEnd, now) : null;
      const nextRunAt = rotation ? computeNextRunAt(sendTime, timezone, now) : null;

      const members = await fetchFallbackMembers(sb);
      return computeForecastFallback({
        members,
        timezone,
        followUps,
        cooldownDays,
        todayBatchAt,
        nextBatchAt,
        nextRunAt,
        now,
      });
    },
    // The forecast changes with every new order and every consent event; keep
    // it fresh but not chatty.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
};
